// Page Plan d'actions — Gestion détaillée d'un diagnostic
// Accessible depuis le tunnel (location.state) ou le dashboard (chargement DB)
// Permet de modifier responsable, dates, statut puis sauvegarder en BDD

import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { trackEvent } from '../lib/analytics'
import { Card, CardContent, CardHeader, CardTitle, Button, Input } from '../components/ui'
import { Badge } from '../components/ui/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table'
import { Checkbox } from '../components/ui/checkbox'
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { api, type SaveActionPlanInput } from '../services/api'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import {
  ClipboardList, CheckCircle2, Clock, AlertTriangle, ArrowRight,
  ListTodo, Trash2, Save, Loader2, ExternalLink, ChevronDown, History,
} from 'lucide-react'
import { cn } from '../lib/utils'

interface LocationState {
  selectedThemes?: string[]
  selectedSolutions?: Array<{ solutionId: string; metierIds: string[] }>
  [key: string]: unknown
}

interface Solution {
  id: string
  code?: string
  theme: string
  typeSolution: string
  libelle: string
  suggestion: string
  lien?: string
  duree?: string
  niveauDifficulte?: string
  metierId?: string
  nature?: string
  coutNiveau?: string
}

interface ActionPlan {
  id: string
  solutionId: string
  theme: string
  libelle: string
  metierIds: string[]
  responsable: string
  dateDebut: string
  dateFin: string
  termineLe?: string
  lien?: string
  duree?: string
  nature?: string
}

// API row shape (from GET /diagnostics/:id/actions)
interface ApiActionRow {
  id: string
  solutionId: string
  metierId: string
  theme: string
  libelle: string
  responsable: string | null
  dateDebut: string | null
  dateFin: string | null
  dateRealisation: string | null
  statut: string
  metier: { code: string; nom: string }
}

// ── Mapping functions ──

/** Convert API rows (1 per métier) into local ActionPlan[] (1 per solution, merged metierIds) */
function fromApiRows(rows: ApiActionRow[]): ActionPlan[] {
  const groups = new Map<string, { firstRow: ApiActionRow; metierCodes: string[] }>()

  for (const row of rows) {
    const key = row.solutionId // group by solutionId ONLY
    if (!groups.has(key)) {
      groups.set(key, { firstRow: row, metierCodes: [] })
    }
    const group = groups.get(key)!
    if (!group.metierCodes.includes(row.metier.code)) {
      group.metierCodes.push(row.metier.code)
    }
  }

  return Array.from(groups.values()).map(({ firstRow, metierCodes }) => ({
    id: firstRow.id, // keep first row's DB id for reference
    solutionId: firstRow.solutionId,
    theme: firstRow.theme,
    libelle: firstRow.libelle,
    metierIds: metierCodes,
    responsable: firstRow.responsable || '',
    dateDebut: firstRow.dateDebut ? firstRow.dateDebut.split('T')[0] : '',
    dateFin: firstRow.dateFin ? firstRow.dateFin.split('T')[0] : '',
    termineLe: firstRow.dateRealisation ? firstRow.dateRealisation.split('T')[0] : undefined,
  }))
}

/** Convert local actions to API payload for PUT /diagnostics/:id/actions */
function toApiPayload(actions: ActionPlan[]): SaveActionPlanInput[] {
  return actions.map(action => ({
    solutionId: action.solutionId,
    metierCodes: action.metierIds,
    theme: action.theme,
    libelle: action.libelle,
    responsable: action.responsable || null,
    dateDebut: action.dateDebut || null,
    dateFin: action.dateFin || null,
    dateRealisation: action.termineLe || null,
    statut: action.termineLe
      ? 'termine' as const
      : (action.responsable && action.dateDebut) ? 'en_cours' as const : 'a_faire' as const,
  }))
}

// Configuration des statuts d'action
type ActionStatus = 'non_commence' | 'en_cours' | 'echu' | 'termine'

const STATUS_CONFIG: Record<ActionStatus, { label: string; icon: typeof CheckCircle2; variant: 'default' | 'secondary' | 'destructive' | 'outline'; tooltip: string }> = {
  non_commence: { label: 'À faire', icon: ListTodo, variant: 'secondary', tooltip: 'Renseignez un responsable et une date de début pour passer en "En cours"' },
  en_cours: { label: 'En cours', icon: Clock, variant: 'default', tooltip: "L'action a un responsable et une date de début" },
  echu: { label: 'Échu', icon: AlertTriangle, variant: 'destructive', tooltip: "La date d'échéance est dépassée" },
  termine: { label: 'Terminé', icon: CheckCircle2, variant: 'outline', tooltip: 'Action terminée' },
}

// Fonction pour calculer le statut automatique d'une action
function getActionStatus(action: ActionPlan): ActionStatus {
  if (action.termineLe) return 'termine'
  if (action.responsable && action.dateDebut) {
    if (action.dateFin && new Date(action.dateFin) < new Date()) return 'echu'
    return 'en_cours'
  }
  return 'non_commence'
}

function capitalizeTheme(theme: string): string {
  if (!theme) return theme
  return theme.charAt(0).toUpperCase() + theme.slice(1)
}

export default function PlanActionsPage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  // Location state (from tunnel navigation)
  const locationState = location.state as LocationState | undefined
  const [selectedSolutions, setSelectedSolutions] = useState<Array<{ solutionId: string; metierIds: string[] }>>(
    locationState?.selectedSolutions || []
  )

  const [actions, setActions] = useState<ActionPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set())
  const [casques, setCasques] = useState<number>(0)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [parentId, setParentId] = useState<string | undefined>(undefined)
  const [parentActions, setParentActions] = useState<ApiActionRow[]>([])
  const [prevTermineesOpen, setPrevTermineesOpen] = useState(false)
  const [showNewBadges, setShowNewBadges] = useState(true)

  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  // Unsaved changes guard
  const { blocker, allowNavigation } = useUnsavedChangesGuard(hasUnsavedChanges)

  const steps = useDiagnosticSteps('plan-actions', diagnosticId, {
    totalMetiers: metierIds.length || 1,
    currentMetierIndex: Math.max(0, metierIds.length - 1),
    q2Completed: true,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress)

  // ── Load data: from location.state (tunnel) OR from DB (dashboard/refresh) ──
  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return
      setIsLoading(true)
      try {
        const diagnostic = await api.getDiagnostic(diagnosticId)
        if (diagnostic?.resultat) {
          setCasques(diagnostic.resultat.casques || 0)
        }
        // Récupérer le plan du parent racine si c'est un diagnostic enfant (mise à jour)
        // Remonter la chaîne parentId pour trouver la racine
        let rootId = diagnostic?.parentId
        if (rootId) {
          try {
            let current = await api.getDiagnostic(rootId)
            while (current?.parentId) {
              rootId = current.parentId
              current = await api.getDiagnostic(rootId)
            }
          } catch { /* ignore — rootId reste le parentId direct */ }
        }
        setParentId(rootId)
        let pActions: ApiActionRow[] = []
        if (rootId) {
          try {
            pActions = await api.getActionPlans(rootId)
            setParentActions(pActions)
          } catch { /* ignore */ }
        }

        // Vérifier si des actions sont déjà sauvegardées en BDD
        const existingRows = await api.getActionPlans(diagnosticId)
        const isFreshFromTunnel = selectedSolutions.length > 0 // location.state present

        if (!isFreshFromTunnel && existingRows.length > 0) {
          // Actions déjà sauvegardées + pas de navigation tunnel → charger depuis la BDD
          setActions(fromApiRows(existingRows))
          setHasUnsavedChanges(false)
          setShowNewBadges(false)
        } else {
          // Restaurer sélections depuis la BDD si pas de location.state
          let currentSolutions = selectedSolutions
          if (currentSolutions.length === 0) {
            const savedSolutions = diagnostic?.selectedSolutions as Array<{ solutionId: string; metierIds: string[] }> | null
            if (savedSolutions && savedSolutions.length > 0) {
              currentSolutions = savedSolutions
              setSelectedSolutions(savedSolutions)
            }
          }

          if (currentSolutions.length > 0) {
            // Build from referentiel (tunnel first pass ou refresh avant première sauvegarde)
            let solutionsRef: Solution[] = []
            const response = await fetch('/data/referentiel/solutions.json')
            if (response.ok) {
              const json = await response.json()
              solutionsRef = json.data || []
            }

            let finalActions: ActionPlan[] = currentSolutions
              .map((sel, index) => {
                const solution = solutionsRef.find((s: Solution) => s.id === sel.solutionId)
                if (!solution) return null
                return {
                  id: `action-${index}-${sel.solutionId}`,
                  solutionId: sel.solutionId,
                  theme: solution.theme,
                  libelle: solution.libelle || solution.suggestion,
                  metierIds: sel.metierIds.length > 0 ? sel.metierIds : (solution.metierId ? [solution.metierId] : metierIds),
                  responsable: '',
                  dateDebut: '',
                  dateFin: '',
                  lien: solution.lien,
                  duree: solution.duree,
                  nature: solution.nature,
                }
              })
              .filter(Boolean) as ActionPlan[]

            // Pré-remplir depuis le plan parent (si mise à jour d'un diagnostic)
            if (parentId && pActions.length > 0) {
              const parentByKey = new Map(pActions.map(a => [a.solutionId, a]))
              finalActions = finalActions.map(action => {
                const inherited = parentByKey.get(action.solutionId)
                if (!inherited) return action
                return {
                  ...action,
                  responsable: inherited.responsable || '',
                  dateDebut: inherited.dateDebut ? inherited.dateDebut.split('T')[0] : '',
                  dateFin: inherited.dateFin ? inherited.dateFin.split('T')[0] : '',
                  termineLe: inherited.dateRealisation ? inherited.dateRealisation.split('T')[0] : undefined,
                }
              })
            }

            setActions(finalActions)
            setHasUnsavedChanges(true) // Fresh from tunnel = not yet saved
          } else {
            // Aucune solution et aucune action sauvegardée
            setHasUnsavedChanges(false)
            setShowNewBadges(false)
          }
        }
      } catch (err) {
        console.error('Erreur chargement données:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save plan to DB ──
  const savePlan = useCallback(async (): Promise<boolean> => {
    if (!diagnosticId || actions.length === 0) return true

    setIsSaving(true)
    try {
      await api.saveActionPlans(diagnosticId, { actions: toApiPayload(actions) })
      setHasUnsavedChanges(false)
      setShowNewBadges(false)
      toast.success('Plan d\'action sauvegardé')
      return true
    } catch (error) {
      console.error('Erreur sauvegarde plan:', error)
      toast.error('Erreur lors de la sauvegarde')
      return false
    } finally {
      setIsSaving(false)
    }
  }, [diagnosticId, actions])

  // ── Action mutations (mark dirty) ──
  const updateAction = (actionId: string, field: keyof ActionPlan, value: string | string[]) => {
    setActions(prev => prev.map(a => a.id === actionId ? { ...a, [field]: value } : a))
    setHasUnsavedChanges(true)
  }

  const removeAction = (actionId: string) => {
    setActions(prev => prev.filter(a => a.id !== actionId))
    setHasUnsavedChanges(true)
    toast('Action supprimée du plan')
  }

  const toggleComplete = (actionId: string) => {
    setActions(prev => prev.map(action => {
      if (action.id !== actionId) return action
      return {
        ...action,
        termineLe: action.termineLe ? undefined : new Date().toISOString().split('T')[0],
      }
    }))
    setHasUnsavedChanges(true)
  }

  const toggleExpand = (actionId: string) => {
    setExpandedActions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(actionId)) newSet.delete(actionId)
      else newSet.add(actionId)
      return newSet
    })
  }

  // Grouper par thème
  const actionsByTheme = useMemo(() => {
    const grouped: Record<string, ActionPlan[]> = {}
    actions.forEach(action => {
      if (!grouped[action.theme]) grouped[action.theme] = []
      grouped[action.theme].push(action)
    })
    return grouped
  }, [actions])

  // Statistiques
  const stats = useMemo(() => {
    const total = actions.length
    const nonCommence = actions.filter(a => getActionStatus(a) === 'non_commence').length
    const enCours = actions.filter(a => getActionStatus(a) === 'en_cours').length
    const echu = actions.filter(a => getActionStatus(a) === 'echu').length
    const termine = actions.filter(a => getActionStatus(a) === 'termine').length
    const progressPercent = total > 0 ? Math.round((termine / total) * 100) : 0
    return { total, themes: Object.keys(actionsByTheme).length, nonCommence, enCours, echu, termine, progressPercent }
  }, [actions, actionsByTheme])

  // Identifiants des solutions du plan parent (pour badge "Nouveau")
  const parentSolutionIds = useMemo(() => new Set(parentActions.map(a => a.solutionId)), [parentActions])

  // Regroupement des actions précédentes par statut (lecture seule)
  const prevActionsContext = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return {
      terminee: parentActions.filter(a => a.statut === 'termine'),
      echu: parentActions.filter(a => a.statut !== 'termine' && !!a.dateFin && new Date(a.dateFin) < today),
      enCours: parentActions.filter(a => {
        if (a.statut === 'termine') return false
        if (a.dateFin && new Date(a.dateFin) < today) return false
        return !!a.responsable
      }),
    }
  }, [parentActions])

  // ── Navigation (save before navigating) ──
  const handleBack = async () => {
    if (hasUnsavedChanges) await savePlan()
    allowNavigation()
    navigate(`/diagnostic/${diagnosticId}/finalisation`, { state: diagnosticState })
  }

  const handleGoToSyntheseFinale = async () => {
    trackEvent('synthese finale')
    if (hasUnsavedChanges) await savePlan()
    allowNavigation()
    navigate(`/diagnostic/${diagnosticId}/synthese-finale`, { state: diagnosticState })
  }

  const handleTerminer = async () => {
    trackEvent('terminer plan action')
    if (hasUnsavedChanges) await savePlan()
    allowNavigation()
    navigate('/dashboard')
  }

  const handleGenerateAttestation = async () => {
    trackEvent('generer attestation')
    if (hasUnsavedChanges) await savePlan()
    allowNavigation()
    navigate(`/diagnostic/${diagnosticId}/attestation`, { state: diagnosticState })
  }

  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleBack}
      onNext={handleTerminer}
      backLabel="Retour"
      nextLabel="Terminer"
      showNavigation={true}
      diagnosticState={diagnosticState}
    >
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <ClipboardList className="size-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">Plan d'actions</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Planifiez la mise en œuvre des solutions sélectionnées
            </p>
          </div>
        </div>
        {/* Save button */}
        {hasUnsavedChanges && actions.length > 0 && (
          <Button onClick={savePlan} disabled={isSaving} size="lg" className="shadow-sm">
            {isSaving ? (
              <><Loader2 className="size-4 mr-1.5 animate-spin" /> Sauvegarde...</>
            ) : (
              <><Save className="size-4 mr-1.5" /> Sauvegarder</>
            )}
          </Button>
        )}
      </div>

      {/* KPI cards */}
      {actions.length > 0 && (
        <div className="mb-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {([
              { count: stats.nonCommence, label: 'À faire', icon: ListTodo, iconBg: 'bg-muted', iconColor: 'text-muted-foreground' },
              { count: stats.enCours, label: 'En cours', icon: Clock, iconBg: 'bg-blue-50', iconColor: 'text-blue-600' },
              { count: stats.termine, label: 'Terminées', icon: CheckCircle2, iconBg: 'bg-green-50', iconColor: 'text-green-600' },
              { count: stats.echu, label: 'Échues', icon: AlertTriangle, iconBg: 'bg-red-50', iconColor: 'text-red-600' },
            ]).map(kpi => {
              const KpiIcon = kpi.icon
              return (
                <Card key={kpi.label}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center gap-3">
                      <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', kpi.iconBg)}>
                        <KpiIcon className={cn('size-5', kpi.iconColor)} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-foreground tabular-nums leading-none">{kpi.count}</p>
                        <p className="text-xs text-muted-foreground mt-1">{kpi.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      )}

      {/* Section lecture seule : actions du diagnostic précédent (uniquement pour une mise à jour) */}
      {parentId && parentActions.length > 0 && (
        <Card className="mb-6 border-muted bg-muted/30">
          <CardHeader className="pb-3 pt-4">
            <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
              <History className="size-4 text-muted-foreground" />
              Actions du diagnostic précédent
              <Badge variant="secondary" className="text-xs font-normal ml-auto">
                Lecture seule
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            {/* Échues */}
            {prevActionsContext.echu.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-destructive flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="size-3.5" />
                  Échues ({prevActionsContext.echu.length})
                </p>
                <div className="space-y-1.5">
                  {prevActionsContext.echu.map(a => (
                    <div key={a.id} className="flex items-center gap-2 pl-2">
                      <span className="text-sm text-muted-foreground flex-1 truncate">{a.libelle}</span>
                      {a.dateFin && (
                        <Badge variant="destructive" className="text-[10px] shrink-0">
                          {new Date(a.dateFin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* En cours */}
            {prevActionsContext.enCours.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-2">
                  <Clock className="size-3.5" />
                  En cours ({prevActionsContext.enCours.length})
                </p>
                <div className="space-y-1.5">
                  {prevActionsContext.enCours.map(a => (
                    <div key={a.id} className="flex items-center gap-2 pl-2">
                      <span className="text-sm text-muted-foreground flex-1 truncate">{a.libelle}</span>
                      {a.responsable && (
                        <span className="text-xs text-muted-foreground/60 shrink-0">{a.responsable}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Terminées — pliable, masquées par défaut */}
            {prevActionsContext.terminee.length > 0 && (
              <div>
                <button
                  onClick={() => setPrevTermineesOpen(o => !o)}
                  className="text-xs font-semibold text-green-700 flex items-center gap-1.5 mb-2 hover:opacity-80 transition-opacity"
                >
                  <CheckCircle2 className="size-3.5" />
                  Terminées ({prevActionsContext.terminee.length})
                  <ChevronDown className={cn('size-3.5 ml-0.5 transition-transform', prevTermineesOpen && 'rotate-180')} />
                </button>
                {prevTermineesOpen && (
                  <div className="space-y-1.5">
                    {prevActionsContext.terminee.map(a => (
                      <div key={a.id} className="flex items-center gap-2 pl-2">
                        <CheckCircle2 className="size-3 text-green-600 shrink-0" />
                        <span className="text-sm text-muted-foreground line-through">{a.libelle}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {actions.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/60 flex items-center justify-center mx-auto mb-5">
              <ClipboardList className="size-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Aucune action dans le plan</h3>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Retournez au diagnostic pour sélectionner des solutions de prévention.
            </p>
            <Button onClick={() => navigate(`/diagnostic/${diagnosticId}/finalisation`)}>
              <ArrowRight className="size-4 mr-1.5" /> Retour au diagnostic
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Info conseil */}
          {stats.nonCommence > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3 mb-5">
              <Clock className="size-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-800">
                Renseignez un <strong>responsable</strong> et une <strong>date de début</strong> pour passer les actions en statut « En cours ».
              </p>
            </div>
          )}

          {/* Desktop table — grouped by theme */}
          <div className="hidden lg:block space-y-4 mb-6">
            {Object.entries(actionsByTheme).map(([theme, themeActions]) => {
              const themeTermine = themeActions.filter(a => !!a.termineLe).length

              return (
              <Card key={theme} className="overflow-hidden pt-0">
                <div className="flex items-center justify-between px-6 py-3 bg-muted/50 border-b">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-primary">
                      {capitalizeTheme(theme)}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      {themeActions.length} action{themeActions.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {themeTermine}/{themeActions.length} terminée{themeTermine > 1 ? 's' : ''}
                  </span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px] pl-6" />
                      <TableHead>Action</TableHead>
                      <TableHead className="w-[120px]">M&eacute;tier</TableHead>
                      <TableHead className="w-[150px]">Responsable</TableHead>
                      <TableHead className="w-[140px]">Date d&eacute;but</TableHead>
                      <TableHead className="w-[140px]">&Eacute;ch&eacute;ance</TableHead>
                      <TableHead className="w-[110px]">Statut</TableHead>
                      <TableHead className="w-[50px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {themeActions.map(action => {
                      const status = getActionStatus(action)
                      const config = STATUS_CONFIG[status]
                      const StatusIcon = config.icon
                      const isComplete = !!action.termineLe

                      return (
                        <TableRow key={action.id} className={cn(
                          'group transition-colors',
                          isComplete && 'bg-green-50/40',
                          status === 'echu' && 'bg-red-50/30',
                        )}>
                          <TableCell className="pl-6">
                            <Checkbox
                              checked={isComplete}
                              onCheckedChange={() => toggleComplete(action.id)}
                              className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={cn(
                                'text-sm font-medium',
                                isComplete && 'line-through text-muted-foreground'
                              )}>
                                {action.libelle}
                              </span>
                              {showNewBadges && parentId && !parentSolutionIds.has(action.solutionId) && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/40 text-primary bg-primary/8">
                                  Nouveau
                                </Badge>
                              )}
                            </div>
                            {action.lien && (
                              <a
                                href={action.lien}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                              >
                                <ExternalLink className="size-3" />
                                Ressource
                              </a>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {action.metierIds.map(mid => {
                                const metier = metiersSelectionnes.find(m => m.code === mid)
                                return (
                                  <Badge key={mid} variant="secondary" className="text-[10px] px-1.5 py-0">
                                    {metier?.nom || mid}
                                  </Badge>
                                )
                              })}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              value={action.responsable}
                              onChange={e => updateAction(action.id, 'responsable', e.target.value)}
                              placeholder="Nom..."
                              className="h-8 text-sm"
                              disabled={isComplete}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="date"
                              value={action.dateDebut}
                              onChange={e => updateAction(action.id, 'dateDebut', e.target.value)}
                              className="h-8 text-sm"
                              disabled={isComplete}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="date"
                              value={action.dateFin}
                              onChange={e => updateAction(action.id, 'dateFin', e.target.value)}
                              className={cn('h-8 text-sm', status === 'echu' && 'border-destructive text-destructive')}
                              disabled={isComplete}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button onClick={() => toggleComplete(action.id)} className="inline-flex">
                                  <Badge variant={config.variant} className={cn(
                                    'cursor-pointer gap-1 transition-all hover:opacity-80',
                                    isComplete && 'bg-green-100 text-green-800 border-green-200'
                                  )}>
                                    <StatusIcon className="size-3" />
                                    {config.label}
                                  </Badge>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p>{config.tooltip}</p>
                                <p className="text-[10px] opacity-70 mt-0.5">
                                  {isComplete ? 'Cliquez pour rouvrir' : 'Cliquez pour marquer terminé'}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                                  title="Supprimer"
                                >
                                  <Trash2 className="size-4" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent size="sm">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Supprimer cette action ?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    L'action sera retir&eacute;e du plan d'action.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                                  <AlertDialogAction variant="destructive" onClick={() => removeAction(action.id)}>
                                    Supprimer
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Card>
              )
            })}
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3 mb-6">
            {actions.map(action => {
              const isExpanded = expandedActions.has(action.id)
              const status = getActionStatus(action)
              const config = STATUS_CONFIG[status]
              const StatusIcon = config.icon
              const isComplete = !!action.termineLe

              return (
                <Card key={action.id} className={cn(
                  'transition-all',
                  isComplete && 'border-green-200 bg-green-50/30',
                  status === 'echu' && 'border-red-200 bg-red-50/20',
                )}>
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => toggleExpand(action.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="pt-0.5" onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={isComplete}
                          onCheckedChange={() => toggleComplete(action.id)}
                          className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {capitalizeTheme(action.theme)}
                          </Badge>
                          <Badge variant={config.variant} className="gap-1 text-[10px] px-1.5 py-0">
                            <StatusIcon className="size-2.5" />
                            {config.label}
                          </Badge>
                          {showNewBadges && parentId && !parentSolutionIds.has(action.solutionId) && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/40 text-primary bg-primary/8">
                              Nouveau
                            </Badge>
                          )}
                        </div>
                        <h3 className={cn(
                          'text-sm font-medium text-foreground',
                          isComplete && 'line-through text-muted-foreground'
                        )}>
                          {action.libelle}
                        </h3>
                      </div>
                      <ChevronDown className={cn(
                        'size-4 text-muted-foreground transition-transform shrink-0 mt-1',
                        isExpanded && 'rotate-180'
                      )} />
                    </div>
                  </div>

                  {isExpanded && (
                    <CardContent className="pt-0 pb-4 space-y-3">
                      {/* M&eacute;tiers */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block mb-1.5">M&eacute;tiers</label>
                        <div className="flex flex-wrap gap-2">
                          {metierIds.map(mid => {
                            const isSelected = action.metierIds.includes(mid)
                            const metier = metiersSelectionnes.find(m => m.code === mid)
                            return (
                              <button
                                key={mid}
                                onClick={() => {
                                  const newIds = isSelected
                                    ? action.metierIds.filter(m => m !== mid)
                                    : [...action.metierIds, mid]
                                  if (newIds.length > 0) updateAction(action.id, 'metierIds', newIds)
                                }}
                                className={cn(
                                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                                  isSelected
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground'
                                )}
                              >
                                {metier?.nom || mid}
                              </button>
                            )
                          })}
                        </div>
                      </div>

                      {/* Responsable */}
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block mb-1.5">Responsable</label>
                        <Input
                          type="text"
                          value={action.responsable}
                          onChange={e => updateAction(action.id, 'responsable', e.target.value)}
                          placeholder="Nom du responsable..."
                          disabled={isComplete}
                        />
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground block mb-1.5">D&eacute;but</label>
                          <Input
                            type="date"
                            value={action.dateDebut}
                            onChange={e => updateAction(action.id, 'dateDebut', e.target.value)}
                            disabled={isComplete}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground block mb-1.5">&Eacute;ch&eacute;ance</label>
                          <Input
                            type="date"
                            value={action.dateFin}
                            onChange={e => updateAction(action.id, 'dateFin', e.target.value)}
                            className={cn(status === 'echu' && 'border-destructive text-destructive')}
                            disabled={isComplete}
                          />
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 pt-1">
                        <Button
                          variant={isComplete ? 'outline' : 'default'}
                          size="sm"
                          className={cn(
                            'flex-1',
                            isComplete && 'border-green-200 text-green-700 hover:bg-green-50'
                          )}
                          onClick={() => toggleComplete(action.id)}
                        >
                          {isComplete ? (
                            <><CheckCircle2 className="size-4 mr-1.5" /> Rouvrir</>
                          ) : (
                            <><CheckCircle2 className="size-4 mr-1.5" /> Terminer</>
                          )}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10">
                              <Trash2 className="size-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent size="sm">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cette action ?</AlertDialogTitle>
                              <AlertDialogDescription>L'action sera retir&eacute;e du plan.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction variant="destructive" onClick={() => removeAction(action.id)}>
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>

                      {action.lien && (
                        <a
                          href={action.lien}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-muted rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <ExternalLink className="size-4" />
                          Voir la ressource OPPBTP
                        </a>
                      )}
                    </CardContent>
                  )}
                </Card>
              )
            })}
          </div>

          {/* Sidebar CTA */}
          <Card className="mb-6 lg:hidden border-dashed">
            <CardContent className="py-4">
              <p className="text-xs text-muted-foreground text-center mb-3">Accès rapide</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={handleGoToSyntheseFinale} className="flex-1 shadow-sm">
                  Synthèse finale <ArrowRight className="size-3 ml-1.5" />
                </Button>
                <Button
                  onClick={handleGenerateAttestation}
                  variant="outline"
                  disabled={casques === 0}
                  className="flex-1"
                >
                  {casques === 0 ? 'Attestation indisponible' : 'Générer l\'attestation'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <UnsavedChangesDialog blocker={blocker} onSave={savePlan} />
    </DiagnosticLayout>
  )
}
